import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CircleDollarSign,
  FolderTree,
  Loader2,
  Package,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardStats } from '../../services/dashboard';
import type { DashboardStats } from '../../types/dashboard';

function formatNumber(value: number): string {
  return value.toLocaleString();
}

const Dashboard = () => {
  const { user, userEmail, userRole } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await getDashboardStats();
        if (!cancelled) setStats(data);
      } catch {
        if (!cancelled) toast.error('Failed to load dashboard data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const displayName = useMemo(() => {
    const name = user?.name?.trim();
    if (name) return name.split(/\s+/)[0] ?? name;
    if (!userEmail) return 'there';
    const local = userEmail.split('@')[0] ?? '';
    const cleaned = local.replace(/[._-]+/g, ' ').trim();
    if (!cleaned) return 'there';
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }, [user?.name, userEmail]);

  const avatarInitial = useMemo(() => {
    if (user?.name?.trim()) return user.name.trim().charAt(0).toUpperCase();
    return displayName.charAt(0).toUpperCase();
  }, [user?.name, displayName]);

  const today = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }),
    [],
  );

  const statCards = useMemo(() => {
    if (!stats) return [];

    const needsAttention = stats.products.outOfStock + stats.products.lowStock;

    return [
      {
        label: 'Total products',
        value: formatNumber(stats.products.total),
        hint: `${formatNumber(stats.products.active)} active · ${formatNumber(stats.products.inactive)} inactive`,
        icon: Package,
        accent: 'from-indigo-500 to-fuchsia-500',
        ring: 'ring-indigo-100',
        href: '/products',
      },
      {
        label: 'Active categories',
        value: formatNumber(stats.categories.active),
        hint: `${formatNumber(stats.categories.total)} categories in catalog`,
        icon: FolderTree,
        accent: 'from-amber-500 to-orange-500',
        ring: 'ring-amber-100',
        href: '/products',
      },
      {
        label: 'Homepage sliders',
        value: formatNumber(stats.sliders.active),
        hint: `${formatNumber(stats.sliders.total)} banners configured`,
        icon: SlidersHorizontal,
        accent: 'from-violet-500 to-purple-500',
        ring: 'ring-violet-100',
        href: '/manage-slider',
      },
      {
        label: 'Stock on hand',
        value: formatNumber(stats.products.totalStock),
        hint:
          needsAttention > 0
            ? `${formatNumber(needsAttention)} product(s) low or out of stock`
            : 'All active products in stock',
        icon: needsAttention > 0 ? AlertTriangle : ShoppingCart,
        accent: needsAttention > 0 ? 'from-rose-500 to-orange-500' : 'from-emerald-500 to-teal-500',
        ring: needsAttention > 0 ? 'ring-rose-100' : 'ring-emerald-100',
        href: '/products',
      },
    ];
  }, [stats]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col w-full">
      <div className="flex flex-1 w-full">
        <Sidebar />
        <main className="flex-1 p-4 md:p-8 w-full max-h-screen overflow-y-auto">
          <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-6 md:p-8 shadow-lg shadow-indigo-200/40 mb-6 md:mb-8">
            <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-fuchsia-300/20 blur-3xl pointer-events-none" />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-xs font-medium text-white/90 mb-3">
                  <Sparkles className="h-3.5 w-3.5" />
                  brixECOM Merchant Portal
                </div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
                  Welcome back, {displayName}
                </h1>
                <p className="mt-1.5 text-sm md:text-base text-white/80">{today}</p>
              </div>
              <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15">
                <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center text-white font-semibold text-sm">
                  {avatarInitial}
                </div>
                <div className="text-white">
                  <p className="text-[11px] uppercase tracking-wider text-white/70">Signed in as</p>
                  <p className="text-sm font-semibold truncate max-w-[12rem]">
                    {user?.name || userEmail || 'User'}
                  </p>
                </div>
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-white/20 text-[11px] font-semibold uppercase tracking-wide text-white capitalize">
                  {userRole}
                </span>
              </div>
            </div>
          </section>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <>
              <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {statCards.map((stat) => (
                  <Link
                    key={stat.label}
                    to={stat.href}
                    className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm transition hover:border-indigo-200 hover:shadow-md"
                  >
                    <div
                      className={`h-11 w-11 rounded-xl bg-gradient-to-br ${stat.accent} flex items-center justify-center shadow-sm ring-4 ${stat.ring} mb-4`}
                    >
                      <stat.icon className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-sm font-medium text-slate-500">{stat.label}</p>
                    <p className="mt-1 text-3xl font-bold text-slate-900 tabular-nums">{stat.value}</p>
                    <p className="mt-2 text-xs text-slate-400">{stat.hint}</p>
                  </Link>
                ))}
              </section>

              {stats && stats.topCategories.length > 0 && (
                <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-base font-semibold text-slate-900">Top categories</h2>
                      <p className="text-sm text-slate-500">By product count in your catalog</p>
                    </div>
                    <Link
                      to="/products"
                      className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      Manage catalog
                    </Link>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {stats.topCategories.map((category, index) => (
                      <li
                        key={category.id}
                        className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-600">
                            {index + 1}
                          </span>
                          <span className="truncate text-sm font-medium text-slate-900">
                            {category.name}
                          </span>
                        </div>
                        <span className="shrink-0 text-sm tabular-nums text-slate-500">
                          {formatNumber(category.products_count)} products
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {stats && stats.products.total === 0 && stats.categories.total === 0 && (
                <section className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
                  <CircleDollarSign className="mx-auto h-10 w-10 text-slate-300" />
                  <p className="mt-3 text-sm font-medium text-slate-700">Your catalog is empty</p>
                  <p className="mt-1 text-sm text-slate-500">
                    Add categories and products to see full dashboard metrics.
                  </p>
                  <Link
                    to="/products"
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                  >
                    <Package className="h-4 w-4" />
                    Go to products
                  </Link>
                </section>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
